import { cn } from "@/utils/utils";
import {
  Tooltip,
  SideSheet,
  Paragraph,
  Card,
  Heading,
  Pane,
  Checkbox
} from "evergreen-ui";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";

import { HTMLProps } from "react";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectItem
} from "./ui/select";
import { DefaultCompressOption } from "@/stores/compress";
import { Mimes } from "@/utils/engines/mimes";
import { SketchPicker } from "react-color";

import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Button } from "./ui/button";
import { Slider } from "./ui/slider";
import { CompressOption } from "@/utils/engines/ImageBase";

export interface OptionItemProps extends HTMLProps<HTMLDivElement> {
  desc?: React.ReactNode;
  children?: React.ReactNode;
}

export function OptionItem(props: OptionItemProps) {
  /* eslint-disable prefer-const */
  let { desc, children, ...extra } = props;
  if (desc && typeof desc === "string") {
    desc = <Paragraph className="mb-2 dark:text-white">{desc}</Paragraph>;
  }
  return (
    <div className="mb-6 dark:text-white" {...extra}>
      <div className="mb-2 dark:text-white">{desc}</div>
      <div>{children}</div>
    </div>
  );
}

function OptionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <Heading size={600} className=" dark:text-white">{children}</Heading>
    </div>
  );
}
const getFormatOptions = () => {
  const options: { label: string; value: string }[] = [];
  Object.keys(Mimes).forEach(mime => {
    if (!["svg", "gif"].includes(mime)) {
      options.push({
        value: mime,
        label: mime.toUpperCase()
      });
    }
  });
  return options;
};
export function CompressFormOption(props: {
  open: boolean;
  setOptions: (options: CompressOption) => void;
  onClose: () => void;
}) {
  const { open, onClose, setOptions } = props;
  const { t } = useTranslation("compress-image");
  const [option, setOption] = useState(DefaultCompressOption);

  const resizeOptions = [
    {
      value: "fitWidth",
      label: t("optionPanel.fitWidth")
    },
    {
      value: "fitHeight",
      label: t("optionPanel.fitHeight")
    },
    {
      value: "specifiedSize",
      label: t("optionPanel.specifiedSize")
    }
  ];

  let input: React.ReactNode = null;
  const resizeMethod = option.resize.method;
  const widthInput = (
    <Input
      type="number"
      min={0}
      step={1}
      placeholder={t("optionPanel.widthPlaceholder")}
      value={option.resize.width}
      onChange={e => {
        setOption({
          ...option,
          resize: {
            ...option.resize,
            width: Number(e.target.value!)
          }
        });
      }}
    />
  );
  const heightInput = (
    <Input
      min={0}
      step={1}
      type="number"
      placeholder={t("optionPanel.heightPlaceholder")}
      value={option.resize.height}
      onChange={e => {
        setOption({
          ...option,
          resize: {
            ...option.resize,
            height: Number(e.target.value!)
          }
        });
      }}
    />
  );
  if (resizeMethod === "fitWidth") {
    input = widthInput;
  } else if (resizeMethod === "fitHeight") {
    input = heightInput;
  } else if (resizeMethod === "specifiedSize") {
    input = (
      <div className="flex flex-col gap-4">
        {widthInput}
        {heightInput}
      </div>
    );
  }
  let colorPicker: React.ReactNode = null;
  const target = option.format.target;

  if (target && ["jpg", "jpeg"].includes(target)) {
    colorPicker = (
      <OptionItem desc={t("optionPanel.transparentFillDesc")}>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant={"outline"}
              className={cn(
                "w-[180px] justify-start text-left font-normal",
                !option.format.transparentFill && "text-muted-foreground"
              )}
            >
              <div
                className={cn(
                  "mr-2 h-4 w-4 border",
                  `text-[${option.format.transparentFill}]`
                )}
              />
              {option.format.transparentFill ? (
                option.format.transparentFill
              ) : (
                <span>Pick a Color</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <SketchPicker
              showText
              disabledAlpha
              value={option.format.transparentFill}
              onChangeComplete={value => {
                setOption({
                  ...option,
                  format: {
                    ...option.format,
                    transparentFill: value.hex?.toUpperCase()
                  }
                });
              }}
            />
          </PopoverContent>
        </Popover>
      </OptionItem>
    );
  }

  return (
    <SideSheet
      containerProps={{ className: "dark:!bg-slate-900 dark:text-white" }}
      isShown={open}
      onCloseComplete={() => onClose()}
    >
      <Pane
        zIndex={1}
        flexShrink={0}
        elevation={0}
        backgroundColor="white"
        className="absolute top-0 left-0 w-full dark:!bg-slate-900 dark:text-white"
      >
        <Pane padding={16} borderBottom="muted">
          <Heading size={600} className="dark:text-white">{t("action.setting")}</Heading>
          <Paragraph size={400} color="muted">
            {t("subtitle")}
          </Paragraph>
        </Pane>
      </Pane>
      <Pane
        flex="1"
        overflowY="scroll"
        background="white"
        padding={16}
        className="my-[80px] dark:!bg-slate-900"
      >
        <div>
          <OptionTitle>{t("optionPanel.resizeLable")}</OptionTitle>
          <OptionItem>
            <Select
              value={option.resize.method}
              onValueChange={value =>
                setOption({
                  ...option,
                  resize: {
                    width: undefined,
                    height: undefined,
                    method: value as "fitWidth" | "fitHeight"
                  }
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("optionPanel.resizePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {resizeOptions.map(item => {
                    return (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
            {input && <div className="mt-4">{input}</div>}
          </OptionItem>
        </div>
        <div>
          <OptionTitle>{t("optionPanel.outputFormat")}</OptionTitle>
          <OptionItem>
            <Select
              value={option.format.target}
              onValueChange={value =>
                setOption({
                  ...option,
                  format: {
                    ...option.format,
                    target: value as any
                  }
                })
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={t("optionPanel.outputFormatPlaceholder")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {getFormatOptions().map(item => {
                    return (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              </SelectContent>
            </Select>
          </OptionItem>
          {colorPicker}
        </div>
        <div>
          <OptionTitle>{t("optionPanel.jpegLable")}</OptionTitle>
          <OptionItem desc={t("optionPanel.qualityTitle")}>
            <Tooltip
              content={
                <span className="text-white">{option.jpeg.quality}</span>
              }
            >
              <Slider
                defaultValue={[DefaultCompressOption.jpeg.quality]}
                value={[option.jpeg.quality]}
                min={0}
                max={1}
                step={0.01}
                onValueChange={value => {
                  setOption({
                    ...option,
                    jpeg: {
                      ...option.jpeg,
                      quality: value?.[0]
                    }
                  });
                }}
              />
            </Tooltip>
          </OptionItem>
        </div>

        <div>
          <OptionTitle>{t("optionPanel.pngLable")}</OptionTitle>
          <OptionItem desc={t("optionPanel.colorsDesc")}>
            <Tooltip
              content={<span className="text-white">{option.png.colors}</span>}
            >
              <Slider
                defaultValue={[DefaultCompressOption.png.colors]}
                value={[option.png.colors]}
                min={2}
                max={256}
                step={1}
                onValueChange={value => {
                  setOption({
                    ...option,
                    png: {
                      ...option.png,
                      colors: value?.[0]
                    }
                  });
                }}
              />
            </Tooltip>
          </OptionItem>
          <OptionItem desc={t("optionPanel.pngDithering")}>
            <Tooltip
              content={
                <span className="text-white">{option.png.dithering}</span>
              }
            >
              <Slider
                defaultValue={[DefaultCompressOption.png.dithering]}
                value={[option.png.dithering]}
                min={0}
                max={1}
                step={0.01}
                onValueChange={value => {
                  setOption({
                    ...option,
                    png: {
                      ...option.png,
                      dithering: value?.[0]
                    }
                  });
                }}
              />
            </Tooltip>
          </OptionItem>
        </div>

        <div>
          <OptionTitle>{t("optionPanel.gifLable")}</OptionTitle>
          <OptionItem>
            <Checkbox
              checked={option.gif.dithering}
              label={t("optionPanel.gifDithering")}
              onChange={event => {
                setOption({
                  ...option,
                  gif: {
                    ...option.gif,
                    dithering: event.target.checked
                  }
                });
              }}
            />
          </OptionItem>
          <OptionItem desc={t("optionPanel.colorsDesc")}>
            <Tooltip
              content={<span className="text-white">{option.gif.colors}</span>}
            >
              <Slider
                defaultValue={[DefaultCompressOption.gif.colors]}
                value={[option.gif.colors]}
                min={2}
                max={256}
                step={1}
                onValueChange={value => {
                  setOption({
                    ...option,
                    gif: {
                      ...option.gif,
                      colors: value?.[0]
                    }
                  });
                }}
              />
            </Tooltip>
          </OptionItem>
        </div>

        {Mimes.avif && (
          <div>
            <OptionTitle>{t("optionPanel.avifLable")}</OptionTitle>
            <OptionItem desc={t("optionPanel.avifQuality")}>
              <Tooltip
                content={
                  <span className="text-white">{option.avif.quality}</span>
                }
              >
                <Slider
                  defaultValue={[DefaultCompressOption.avif.quality]}
                  value={[option.avif.quality]}
                  min={1}
                  max={100}
                  step={1}
                  onValueChange={value => {
                    setOption({
                      ...option,
                      avif: {
                        ...option.avif,
                        quality: value?.[0]
                      }
                    });
                  }}
                />
              </Tooltip>
            </OptionItem>
            <OptionItem desc={t("optionPanel.avifSpeed")}>
              <Tooltip
                content={
                  <span className="text-white">{option.avif.speed}</span>
                }
              >
                <Slider
                  defaultValue={[DefaultCompressOption.avif.speed]}
                  value={[option.avif.speed]}
                  min={1}
                  max={100}
                  step={1}
                  onValueChange={value => {
                    setOption({
                      ...option,
                      avif: {
                        ...option.avif,
                        speed: value?.[0]
                      }
                    });
                  }}
                />
              </Tooltip>
            </OptionItem>
          </div>
        )}
      </Pane>
      <div className="dark:!bg-slate-900 flex bottom-0 left-0 z-1 bg-white w-full absolute items-center border-t py-4 px-6 justify-end">
        <div className="flex gap-2">
          <Button
            className="w-[180px]"
            size="default"
            variant="outline"
            onClick={() => {
              setOptions(DefaultCompressOption);
              setOption(DefaultCompressOption);
              onClose();
            }}
          >
            {t("optionPanel.resetBtn")}
          </Button>
          <Button
            variant="default"
            className="w-[180px]"
            size="default"
            onClick={() => {
              setOptions(option);
              onClose();
            }}
          >
            {t("optionPanel.confirmBtn")}
          </Button>
        </div>
      </div>
    </SideSheet>
  );
}
